import type { VehicleId } from '../../types/gcs/vehicle';
import type { GcsData } from '../../types/gcs/gcsData';
import type { MissionFlightDataNotification } from '../../types/gcs/mission';

interface FollowedVehicleRef {
  gcsId: string;
  vehicleId: VehicleId;
}

export interface FollowedVehicleData {
  flightData: MissionFlightDataNotification | null;
  hasPackage: boolean | null;
}

let lastFollowedVehicleKey: string | null = null;
let lastFlightData: MissionFlightDataNotification | null = null;
let lastHasPackage: boolean | null = null;
let lastFollowedVehicleData: FollowedVehicleData | null = null;

/**
 * Selector to get mission-related data for the currently followed vehicle.
 * @param data - The GCS data map from the store
 * @param followedVehicle - The followed vehicle reference
 * @returns Followed vehicle mission data, or null if unavailable
 */
export function selectFollowedVehicleData(
  data: Map<string, GcsData>,
  followedVehicle: FollowedVehicleRef | null
): FollowedVehicleData | null {
  if (!followedVehicle) {
    lastFollowedVehicleKey = null;
    lastFlightData = null;
    lastHasPackage = null;
    lastFollowedVehicleData = null;
    return null;
  }

  const gcsData = data.get(followedVehicle.gcsId);
  if (!gcsData) {
    return null;
  }

  const liveMission = Array.from(gcsData.liveMissionsMap.values()).find(
    (mission) => mission.vehicleId === followedVehicle.vehicleId
  );

  if (!liveMission) {
    lastFollowedVehicleKey = null;
    lastFlightData = null;
    lastHasPackage = null;
    lastFollowedVehicleData = null;
    return null;
  }

  const missionId = liveMission.missionId;
  const followedVehicleKey = `${followedVehicle.gcsId}:${followedVehicle.vehicleId}:${missionId}`;
  const flightData = gcsData.missionFlightDataMap.get(missionId) ?? null;
  const hasPackage = gcsData.missionDetailedStateMap.get(missionId)?.hasPackage ?? null;

  if (
    lastFollowedVehicleData &&
    lastFollowedVehicleKey === followedVehicleKey &&
    lastFlightData === flightData &&
    lastHasPackage === hasPackage
  ) {
    return lastFollowedVehicleData;
  }

  lastFollowedVehicleKey = followedVehicleKey;
  lastFlightData = flightData;
  lastHasPackage = hasPackage;
  lastFollowedVehicleData = {
    flightData,
    hasPackage
  };

  return lastFollowedVehicleData;
}
